(function () {
  // Sets up a global object to hold custom functions and classes.
  window.mx = window.mx || {};

  window.mx.throttle = (callback) => {
    let requestId = null;
    let lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  };

  window.mx.debounce = (callback, delay = 100) => {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callback.apply(this, args);
      }, delay);
    };
  };

  window.mx.MxSlider = class extends HTMLUListElement {
    cachedConfig = null;
    eventHandlerList = [];
    pendingScroll = false;
    activeIndex = 0;
    isDragging = false;
    dragPosition = {};
    nearingStart = false;
    leavingStart = true;
    nearingEnd = true;
    leavingEnd = false;

    constructor() {
      super();

      if (this.slideList.length === 0) return;

      this.addEventListener(
        "scroll",
        window.mx.throttle(() => this.onScroll()),
        { passive: true },
      );
      this.addEventListener("scrollend", () => this.onScrollEnd());

      new ResizeObserver(this.onResize).observe(this);

      if (this.allowDrag) {
        const mediaQuery = window.matchMedia("screen and (pointer: fine)");

        mediaQuery.addEventListener("change", (event) => {
          if (event.matches) {
            this.addEventListener("pointerdown", this.onPointerDown);
          } else {
            this.removeEventListener("pointerdown", this.onPointerDown);
          }
        });

        if (mediaQuery.matches) {
          this.addEventListener("pointerdown", this.onPointerDown);
        }
      } else {
        this.style.setProperty("cursor", "auto");
      }
    }

    connectedCallback() {
      this.activeIndex = Math.max(
        0,
        this.slideList.findIndex((slide) => slide.hasAttribute("data-initial")),
      );

      if (this.activeIndex > 0) {
        this.select(this.activeIndex, "auto");
      }

      if (this.adaptiveHeight) {
        this.adaptHeight();
      }
    }

    get config() {
      if (this.cachedConfig === null) {
        const configElement = document.querySelector(`#${this.id}__config`);

        try {
          this.cachedConfig = configElement
            ? JSON.parse(configElement.textContent)
            : {};
        } catch (error) {
          console.error("Error parsing slider JSON configuration", this, error);
        }
      }

      return this.cachedConfig;
    }
    get slideList() {
      return Array.from(this.children);
    }
    get activeSlide() {
      return this.slideList[this.activeIndex];
    }
    get fullyVisibleSlideCount() {
      return [...this.slideList]
        .map((slide) => {
          const slideLeft = slide.offsetLeft;
          const slideRight = slideLeft + slide.clientWidth;

          return (
            slideRight <= this.scrollLeft + this.clientWidth &&
            slideLeft >= this.scrollLeft
          );
        })
        .filter((visibleSlide) => visibleSlide).length;
    }
    get allowDrag() {
      return this.config?.allowDrag ?? true;
    }
    get loopOver() {
      return this.config?.loopOver ?? false;
    }
    get adaptiveHeight() {
      return this.config?.adaptiveHeight ?? false;
    }

    previous(behavior) {
      if (this.loopOver && this.leavingStart) {
        this.select(
          this.slideList.length - this.fullyVisibleSlideCount,
          behavior,
        );
      } else {
        this.select(Math.max(this.activeIndex - 1, 0), behavior);
      }
    }
    next(behavior) {
      if (this.loopOver && this.leavingEnd) {
        this.select(0, behavior);
      } else {
        this.select(
          Math.min(this.activeIndex + 1, this.slideList.length - 1),
          behavior,
        );
      }
    }
    select(index, behavior) {
      if (
        !(index in this.slideList) ||
        !this.checkVisibility() ||
        this.offsetWidth === 0 ||
        this.offsetHeight === 0
      )
        return;

      this.dispatchEvent(
        new CustomEvent("mx-slider:select", {
          detail: { index, slide: this.slideList[index] },
        }),
      );

      this.updateActiveIndex(index);
      this.pendingScroll = true;
      this.scrollTo({
        left: this.calculateLeftScroll(this.slideList[index]),
        behavior: behavior,
      });
    }
    updateActiveIndex(index) {
      if (index === this.activeIndex) return;

      this.activeIndex = index;

      this.dispatchEvent(
        new CustomEvent("mx-slider:change", {
          detail: { index: this.activeIndex, slide: this.activeSlide },
        }),
      );
    }
    calculateLeftScroll(slide) {
      const scrollPadding = getComputedStyle(this).scrollPaddingInlineStart;
      const scrollPaddingValue =
        scrollPadding === "auto" ? 0 : parseInt(scrollPadding);
      const scrollLeft = slide.offsetLeft - scrollPaddingValue;

      return Math.min(
        Math.max(scrollLeft, 0),
        this.scrollWidth - this.clientWidth,
      );
    }
    calculateClosestIndex() {
      const cellOffsetList = this.slideList.map((slide) => slide.offsetLeft);

      return cellOffsetList.indexOf(
        cellOffsetList.reduce((prev, curr) =>
          Math.abs(curr - this.scrollLeft) < Math.abs(prev - this.scrollLeft)
            ? curr
            : prev,
        ),
      );
    }
    adaptHeight() {
      if (this.clientHeight === this.activeSlide.clientHeight) return;

      this.style.removeProperty("max-height");
      if (this.scrollHeight != this.clientHeight) {
        this.style.maxHeight = `${this.activeSlide.clientHeight}px`;
      }
    }
    onScroll() {
      const scrollEdgeThreshold = 100;

      if (this.scrollLeft < scrollEdgeThreshold && this.nearingStart) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-nearing", { detail: "start" }),
        );
        this.nearingStart = false;
        this.leavingStart = true;
      }

      if (this.scrollLeft >= scrollEdgeThreshold && this.leavingStart) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-leaving", { detail: "start" }),
        );
        this.nearingStart = true;
        this.leavingStart = false;
      }

      if (
        this.scrollWidth - this.clientWidth <
          this.scrollLeft + scrollEdgeThreshold &&
        this.nearingEnd
      ) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-nearing", { detail: "end" }),
        );
        this.nearingEnd = false;
        this.leavingEnd = true;
      }

      if (
        this.scrollWidth - this.clientWidth >=
          this.scrollLeft + scrollEdgeThreshold &&
        this.leavingEnd
      ) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-leaving", { detail: "end" }),
        );
        this.leavingEnd = false;
        this.nearingEnd = true;
      }
    }
    onScrollEnd() {
      this.pendingScroll = false;

      if (
        this.isDragging ||
        this.activeIndex <= this.slideList.length - this.fullyVisibleSlideCount
      ) {
        this.updateActiveIndex(this.calculateClosestIndex());
      }

      this.dispatchEvent(
        new CustomEvent("mx-slider:settle", {
          detail: { index: this.activeIndex, slide: this.activeSlide },
        }),
      );
    }
    onPointerDown(event) {
      this.dragPosition = {
        left: this.scrollLeft,
        top: this.scrollTop,
        x: event.clientX,
        y: event.clientY,
      };

      this.isDragging = true;

      document.addEventListener("pointermove", this.onPointerMove);
      document.addEventListener("pointerup", this.onPointerUp);
      this.addEventListener("click", this.onClick, { once: true });
    }
    onPointerMove = (event) => {
      if (!this.isDragging) return;

      event.preventDefault();

      const [dx, dy] = [
        event.clientX - this.dragPosition.x,
        event.clientY - this.dragPosition.y,
      ];

      this.scrollTop = this.dragPosition.top - dy;
      this.scrollLeft = this.dragPosition.left - dx;
    };
    onClick(event) {
      if (event.clientX - this.dragPosition.x !== 0) {
        event.preventDefault();
      }
    }
    onPointerUp = () => {
      this.isDragging = false;

      if (!this.pendingScroll) {
        this.scrollTo({
          left: this.calculateLeftScroll(this.activeSlide),
          behavior: "smooth",
        });
      }

      document.removeEventListener("pointermove", this.onPointerMove);
      document.removeEventListener("pointerup", this.onPointerUp);
    };
    onResize() {
      if (this.activeIndex !== this.calculateClosestIndex) {
        this.select(this.activeIndex, "smooth");
      }

      if (this.adaptiveHeight) {
        this.adaptHeight();
      }
    }
  };
  if (!window.customElements.get("mx-slider")) {
    window.customElements.define("mx-slider", window.mx.MxSlider, {
      extends: "ul",
    });
  }

  window.mx.MxSliderNavigation = class extends HTMLUListElement {
    cachedSlider = null;
    eventHandlerList = [];

    constructor() {
      super();
    }

    connectedCallback() {
      if (!this.slider) {
        throw new Error(
          "Slider navigation component requires an aria-controls attribute that refers to the controlled slider.",
        );
      }

      this.navigationItemList.forEach((navigationItem, index) => {
        navigationItem.addEventListener("click", () => this.onClick(index));
      });

      this.slider.addEventListener("mx-slider:change", (event) =>
        this.onNavigationChange(event.detail.index),
      );
    }

    get slider() {
      if (this.cachedSlider === null) {
        const sliderElement = document.getElementById(
          this.getAttribute("aria-controls"),
        );
        if (!sliderElement) return null;

        this.cachedSlider = sliderElement;
      }

      return this.cachedSlider;
    }
    get navigationItemList() {
      return Array.from(this.children);
    }

    onClick(index) {
      this.slider.select(index, "smooth");
      this.onNavigationChange(index);
    }
    onNavigationChange(newIndex) {
      this.navigationItemList.forEach((navigationItem, index) => {
        navigationItem.setAttribute(
          "aria-current",
          index === newIndex ? "true" : "false",
        );
      });

      if (this.scrollWidth !== this.clientWidth) {
        this.scrollTo({
          left:
            this.navigationItemList[newIndex].offsetLeft -
            this.clientWidth / 2 +
            this.navigationItemList[newIndex].clientWidth / 2,
          behavior: "smooth",
        });
      } else if (this.scrollHeight !== this.clientHeight) {
        this.scrollTo({
          top:
            this.navigationItemList[newIndex].offsetTop -
            this.clientHeight / 2 +
            this.navigationItemList[newIndex].clientHeight / 2,
          behavior: "smooth",
        });
      }
    }
  };
  if (!window.customElements.get("mx-slider-navigation")) {
    window.customElements.define(
      "mx-slider-navigation",
      window.mx.MxSliderNavigation,
      {
        extends: "ul",
      },
    );
  }

  window.mx.UpdatedTimeline = class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.slider.addEventListener("mx-slider:select", (event) =>
        this.onSliderSelect(event),
      );

      this.slider.addEventListener("mx-slider:settle", (event) =>
        this.onSliderSettle(event),
      );

      this.querySelectorAll(".timeline-arrow").forEach((arrow) => {
        arrow.addEventListener("click", () => this.onArrowClick(arrow));
      });

      this.slider
        .querySelectorAll(".timeline-milestone-container")
        .forEach((slide) => {
          slide.addEventListener("click", (event) => this.onSlideClick(event));
        });
    }

    get slider() {
      return this.querySelector("[is='mx-slider']");
    }

    onSliderSelect(event) {
      const activeSlide = event.detail.slide;

      this.slider.querySelectorAll("video").forEach((video) => {
        video.pause();
      });

      this.slider.slideList.forEach((slide) => {
        if (slide === activeSlide) {
          slide.setAttribute("aria-current", "true");
          const associatedVideo = slide?.querySelector("video");
          if (associatedVideo) {
            associatedVideo.currentTime = 0;
            associatedVideo.play();
          }
        } else {
          slide.setAttribute("aria-current", "false");
        }
      });

      this.querySelectorAll(".timeline-arrow").forEach((arrow) => {
        arrow.removeAttribute("disabled");
      });

      if (event.detail.index === 0) {
        this.querySelector(".timeline-arrow[data-previous]").setAttribute(
          "disabled",
          "",
        );
      } else if (event.detail.index === this.slider.slideList.length - 1) {
        this.querySelector(".timeline-arrow[data-next]").setAttribute(
          "disabled",
          "",
        );
      }
    }
    onSliderSettle(event) {
      const activeSlide = event.detail.slide;

      this.slider.querySelectorAll("video").forEach((video) => {
        video.pause();
      });

      this.slider.slideList.forEach((slide) => {
        if (slide === activeSlide) {
          slide.setAttribute("aria-current", "true");
          const associatedVideo = slide?.querySelector("video");
          if (associatedVideo) {
            associatedVideo.currentTime = 0;
            associatedVideo.play();
          }
        } else {
          slide.setAttribute("aria-current", "false");
        }
      });
    }
    onArrowClick(arrow) {
      arrow.hasAttribute("data-next")
        ? this.slider.next()
        : this.slider.previous();

      this.querySelectorAll(".timeline-arrow").forEach((arrow) => {
        arrow.removeAttribute("disabled");
      });

      this.slider.activeIndex === this.slider.slideList.length - 1 ||
      this.slider.activeIndex === 0
        ? arrow.setAttribute("disabled", true)
        : arrow.removeAttribute("disabled");
    }
    onSlideClick(event) {
      this.slider.select(
        this.slider.slideList.findIndex((slide) => {
          return slide === event.currentTarget;
        }),
      );
    }
  };
  if (!window.customElements.get("updated-timeline")) {
    window.customElements.define("updated-timeline", window.mx.UpdatedTimeline);
  }
})();
